import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import type { MenuCommand, MenuGroup } from '../../lib/fileActions';
import { Icon } from './Icon';

export interface ContextMenuProps {
  groups: MenuGroup[];
  /** Viewport coordinates: the pointer, or under the focused row when opened from the keyboard. */
  x: number;
  y: number;
  label: string;
  onSelect: (command: MenuCommand) => void;
  onClose: () => void;
}

/**
 * WAI-ARIA menu at a point: arrow keys, Home/End, first-letter type-ahead, Enter/Space, Escape.
 * Portalled to <body> with fixed positioning because the files pane and sidebar clip overflow, and
 * clamped to the viewport once measured (hidden until then so it never flashes off-screen).
 */
export function ContextMenu({ groups, x, y, label, onSelect, onClose }: ContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y, ready: false });
  const items = () => Array.from(ref.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? []);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    setPos({
      left: Math.max(8, Math.min(x, window.innerWidth - width - 8)),
      top: Math.max(8, Math.min(y, window.innerHeight - height - 8)),
      ready: true,
    });
  }, [x, y]);

  // Only once visible: browsers ignore focus() on a visibility:hidden element (jsdom does not).
  useEffect(() => {
    if (pos.ready) items()[0]?.focus();
  }, [pos.ready]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', onClose);
    window.addEventListener('blur', onClose);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', onClose);
      window.removeEventListener('blur', onClose);
    };
  }, [onClose]);

  const onKeyDown = (e: KeyboardEvent) => {
    const list = items();
    const i = list.findIndex((el) => el === document.activeElement);
    const focus = (n: number) => list[(n + list.length) % list.length]?.focus();
    if (e.key === 'ArrowDown') focus(i + 1);
    else if (e.key === 'ArrowUp') focus(i - 1);
    else if (e.key === 'Home') focus(0);
    else if (e.key === 'End') focus(list.length - 1);
    else if (e.key === 'Escape' || e.key === 'Tab') onClose();
    else if (e.key.length === 1 && /\S/.test(e.key)) {
      const k = e.key.toLowerCase();
      [...list.slice(i + 1), ...list.slice(0, i + 1)].find((el) => el.textContent?.trim().toLowerCase().startsWith(k))?.focus();
    } else return;
    e.preventDefault();
  };

  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label={label}
      className="gsl-ctx-menu"
      style={{ left: pos.left, top: pos.top, visibility: pos.ready ? 'visible' : 'hidden' }}
      onKeyDown={onKeyDown}
      onContextMenu={(e) => e.preventDefault()}
    >
      {groups.map((group, gi) => (
        <Fragment key={gi}>
          {gi > 0 ? <div role="separator" className="gsl-ctx-sep" /> : null}
          {group.map((item) => (
            <button
              key={item.command}
              type="button"
              role="menuitem"
              tabIndex={-1}
              className={`gsl-menu-item${item.danger ? ' is-danger' : ''}`}
              onClick={() => {
                onClose();
                onSelect(item.command);
              }}
            >
              <Icon name={item.icon} size={15} />
              {item.label}
            </button>
          ))}
        </Fragment>
      ))}
    </div>,
    document.body,
  );
}
